/**
 * V54.10: 직원 선택 패널 컴포넌트
 *
 * 좌석에 직원을 지정하기 위한 검색/선택 패널입니다.
 *
 * V54.10 변경사항:
 * - 직함 표시 규칙: 팀원/담당 → 직위(positionTitle), 팀장 이상 → 직책(jobTitle)
 *
 * V54.7 변경사항:
 * - 검색 전에는 목록 비움 (입력 후부터 검색 결과 표시)
 *
 * @module pages/organization/components/seatMap/EmployeePickerPanel
 */

import React, { useState, useCallback, useMemo } from 'react';
import { X, Search, User, UserMinus } from 'lucide-react';

import { CompanyCode } from '../../../../shared/types/seatMap';
import { useEmployees } from '../../../../shared/hooks/useEmployees';

// ============================================
// V54.10: 직함 표시 헬퍼
// ============================================

/**
 * V54.10: 직원의 표시용 직함 결정
 * - 팀장 이상(대표, 본부장, 실장, 팀장) → 직책(jobTitle) 반환
 * - 팀원/담당 → 직위(positionTitle) 반환
 */
const LEADER_JOB_TITLES = ['대표', '본부장', '실장', '팀장'];

function getDisplayPosition(jobTitle?: string, positionTitle?: string): string | undefined {
  // 직책이 팀장 이상이면 직책 표시
  if (jobTitle && LEADER_JOB_TITLES.includes(jobTitle)) {
    return jobTitle;
  }
  // 그 외(팀원, 담당 등)는 직위 표시
  return positionTitle;
}

// ============================================
// Props 타입
// ============================================

interface EmployeePickerPanelProps {
  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 현재 배정된 직원 ID */
  currentEmployeeId?: string;

  /** 현재 배정된 직원 이름 */
  currentEmployeeName?: string;

  /** V52.1: 이미 다른 좌석에 배치된 직원 ID 목록 (현재 좌석 제외) */
  assignedEmployeeIds?: string[];

  /** 직원 지정 핸들러 */
  onAssign: (employee: { id: string; name: string; position?: string } | null) => void;

  /** 패널 닫기 핸들러 */
  onClose: () => void;
}

// ============================================
// 패널 컴포넌트
// ============================================

export function EmployeePickerPanel({
  companyCode,
  currentEmployeeId,
  currentEmployeeName,
  assignedEmployeeIds = [],
  onAssign,
  onClose,
}: EmployeePickerPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // V64: 자리배치도 편집 모드 전용 검색 경로 사용
  // - 권한은 서버에서 checkSeatMapEditPermission으로 검증 (편집 권한 SSoT 일치)
  // - role_manager(조직장)도 본인 회사 자리배치도 직원 검색 가능
  const { employees, isLoading } = useEmployees({
    requestedCompanyCode: companyCode,
    forSeatMap: true,
  });

  // V54.7: 검색 필터링 - 입력 전에는 목록 비움
  const filteredEmployees = useMemo(() => {
    if (!employees) return [];

    const query = searchQuery.toLowerCase().trim();
    // V54.7: 입력 전에는 빈 배열 반환 (전체 목록 미노출)
    if (!query) return [];

    return employees.filter(
      (emp) =>
        emp.nameKo?.toLowerCase().includes(query) ||
        emp.positionTitle?.toLowerCase().includes(query) ||
        emp.orgUnitName?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

  // 직원 선택 핸들러
  const handleSelectEmployee = useCallback(
    (emp: { id: string; name: string; position?: string }) => {
      onAssign({
        id: emp.id,
        name: emp.name,
        position: emp.position,
      });
    },
    [onAssign]
  );

  // 배정 해제 핸들러
  const handleUnassign = useCallback(() => {
    onAssign(null);
  }, [onAssign]);

  // V54.9: 패널 내부 클릭 이벤트가 캔버스로 전파되지 않도록 차단
  const handlePanelMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      onMouseDown={handlePanelMouseDown}
      style={{
        width: '280px',
        background: 'white',
        borderLeft: '1px solid var(--color-gray-200)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--color-gray-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <User size={18} color="#3b82f6" />
          <span
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1e293b',
            }}
          >
            직원 지정
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            padding: '4px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderRadius: '4px',
          }}
        >
          <X size={18} color="#64748b" />
        </button>
      </div>

      {/* 현재 배정 상태 */}
      {currentEmployeeId && (
        <div
          style={{
            padding: '12px 16px',
            background: '#f0f9ff',
            borderBottom: '1px solid #e0f2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#64748b',
              }}
            >
              현재 배정
            </div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#0369a1',
              }}
            >
              {currentEmployeeName || currentEmployeeId}
            </div>
          </div>
          <button
            onClick={handleUnassign}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '6px 10px',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              background: 'white',
              color: '#dc2626',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            <UserMinus size={14} />
            해제
          </button>
        </div>
      )}

      {/* 검색 */}
      <div style={{ padding: '12px 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: '#f8fafc',
          }}
        >
          <Search size={16} color="#94a3b8" />
          <input
            type="text"
            placeholder="이름, 직책, 부서 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* 직원 목록 */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 16px 16px',
        }}
      >
        {isLoading ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
            }}
          >
            로딩 중...
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              color: '#94a3b8',
              fontSize: '13px',
            }}
          >
            {searchQuery ? '검색 결과가 없습니다' : '이름을 입력하여 검색하세요'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {filteredEmployees.map((emp) => {
              const isSelected = emp.id === currentEmployeeId;
              const displayName = emp.nameKo || '(이름 없음)';
              // V52.1: 이미 다른 좌석에 배치된 직원인지 확인
              const isAlreadyAssigned = assignedEmployeeIds.includes(emp.id);

              return (
                <button
                  key={emp.id}
                  onClick={() =>
                    handleSelectEmployee({
                      id: emp.id,
                      name: displayName,
                      // V54.10: 직함 표시 규칙 적용
                      position: getDisplayPosition(emp.jobTitle, emp.positionTitle),
                    })
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    background: isSelected ? '#eff6ff' : isAlreadyAssigned ? '#fef3c7' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#93c5fd';
                      e.currentTarget.style.background = isAlreadyAssigned ? '#fef3c7' : '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.background = isAlreadyAssigned ? '#fef3c7' : 'white';
                    }
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: isAlreadyAssigned ? '#fde68a' : '#e0e7ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <User size={18} color={isAlreadyAssigned ? '#b45309' : '#4f46e5'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#1e293b',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {displayName}
                      {/* V52.1: 이미 배치된 직원 표시 */}
                      {isAlreadyAssigned && (
                        <span style={{ color: '#b45309', fontSize: '11px', marginLeft: '6px' }}>
                          (이동됨)
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#64748b',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {/* V54.10: 직함 표시 규칙 적용 */}
                      {[getDisplayPosition(emp.jobTitle, emp.positionTitle), emp.orgUnitName].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
